import { ArrowRight, CheckCircle2, Info } from "lucide-react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const BEFORE = [
  "Reporting at an aggregated level (customer's VAT ID)",
  "Monthly or quarterly",
  "No detailed invoice data",
  "Risk of VAT fraud remains high",
];

const AFTER = [
  "Transaction-based digital reporting (near real-time)",
  "Transmission of invoice data (e-invoice)",
  "Uniform digital standard formats across the EU",
  "More transparency, less fraud, fairer competition",
  "E-invoicing mandatory across the EU single market",
];

const STAGES: Array<{ title: string; detail: string; date: string }> = [
  {
    title: "Legislation",
    detail: "Adoption of the ViDA directive by the EU Council",
    date: "11 March 2025 (Council adoption)",
  },
  {
    title: "National transposition",
    detail: "EU member states transpose ViDA into national law",
    date: "by 31 December 2025 (transposition into national law)",
  },
  {
    title: "E-invoicing (B2B)",
    detail: "Mandatory e-invoicing across the EU single market",
    date: "from 1 July 2030",
  },
  {
    title: "Digital reporting (DRR)",
    detail: "Transaction-based digital reporting, near real-time",
    date: "from 1 July 2030 (at the latest), possibly earlier",
  },
  {
    title: "Further development",
    detail: "EU-wide standards, extensions and optimizations",
    date: "ongoing from 2030",
  },
];

const BUSINESS_IMPACT: Array<[string, string, string]> = [
  ["Reporting scope", "Aggregated (by VAT ID)", "Transaction-based (detailed invoice data)"],
  ["Reporting timing", "Monthly / quarterly", "Near real-time (within a few days)"],
  [
    "Invoice format",
    "Paper / PDF / EDI (freely chosen)",
    "E-invoicing mandatory (structured formats)",
  ],
  [
    "System requirements",
    "Lower requirements",
    "Higher requirements for data quality, IT processes and integration",
  ],
  [
    "Risk of non-compliance",
    "Fines, back payments",
    "Higher fines, possible loss of input VAT deduction",
  ],
  ["Opportunities", "–", "Process automation, fewer errors, transparency, competitive advantage"],
];

const SAP_IMPACT: Array<[string, string]> = [
  [
    "Data model & master data",
    "Extension of invoice data (e.g. additional mandatory fields), high requirements for data quality",
  ],
  [
    "Processes & integration",
    "Adjustments to Order-to-Cash and Procure-to-Pay processes, integration with tax authority reporting systems",
  ],
  [
    "SAP solutions",
    "Use and extension of SAP S/4HANA, SAP Document Compliance (eDocument Cockpit), SAP Integration Suite",
  ],
  [
    "E-invoicing capability",
    "Support for structured e-invoicing formats (e.g. EN 16931, Peppol BIS)",
  ],
  [
    "Compliance & reporting",
    "Ensuring digital reporting (DRR), archiving, traceability and audit readiness",
  ],
  [
    "Action required",
    "Early project planning, gap analysis, system adjustments, and testing & go-live well ahead of 2030",
  ],
];

/**
 * Visual overview of the EU ViDA reform. Rendered only inside the English
 * article "vida-eu-vat-digital-age".
 */
export function ViDAOverviewGraphicEn() {
  return (
    <section
      aria-labelledby="vida-overview-en"
      className="rounded-2xl border border-border bg-card p-6 text-foreground md:p-8"
    >
      <h2 id="vida-overview-en" className="text-2xl font-bold md:text-3xl">
        ViDA at a Glance – Stages, Deadlines and Impact on SAP Landscapes
      </h2>
      <p className="mt-3 text-muted-foreground">
        ViDA replaces the periodic summary VAT return with transaction-based digital reporting and
        makes e-invoicing mandatory across the EU single market.
      </p>

      <div className="mt-8 grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-border p-5">
          <h3 className="text-base font-bold">Before (Recapitulative Statement)</h3>
          <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
            {BEFORE.map((item) => (
              <li key={item} className="flex gap-2">
                <span
                  aria-hidden="true"
                  className="mt-2 size-1.5 shrink-0 rounded-full bg-muted-foreground"
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-primary/40 bg-primary/5 p-5">
          <h3 className="text-base font-bold text-primary">Going forward (ViDA)</h3>
          <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
            {AFTER.map((item) => (
              <li key={item} className="flex gap-2">
                <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden="true" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <h3 className="mt-10 text-lg font-bold">Stages and Deadlines</h3>
      <ol className="mt-4 flex flex-col gap-4 md:flex-row md:flex-wrap">
        {STAGES.map((stage, index) => (
          <li
            key={stage.title}
            className="flex flex-1 items-start gap-3 rounded-xl border border-border p-4 md:min-w-[15rem]"
          >
            <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
              {index + 1}
            </span>
            <span>
              <span className="block text-sm font-bold">{stage.title}</span>
              <span className="mt-1 block text-sm text-muted-foreground">{stage.detail}</span>
              <span className="mt-2 flex items-center gap-1 text-sm font-medium text-primary">
                <ArrowRight className="size-3.5" aria-hidden="true" />
                {stage.date}
              </span>
            </span>
          </li>
        ))}
      </ol>

      <h3 className="mt-10 text-lg font-bold">Impact on Businesses</h3>
      <div className="mt-4 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Criterion</TableHead>
              <TableHead>Before</TableHead>
              <TableHead>Going forward (ViDA)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {BUSINESS_IMPACT.map(([criterion, before, after]) => (
              <TableRow key={criterion}>
                <TableCell className="font-medium">{criterion}</TableCell>
                <TableCell className="text-muted-foreground">{before}</TableCell>
                <TableCell className="text-muted-foreground">{after}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <h3 className="mt-10 text-lg font-bold">Impact on Existing SAP Landscapes</h3>
      <div className="mt-4 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Area</TableHead>
              <TableHead>Description</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {SAP_IMPACT.map(([area, description]) => (
              <TableRow key={area}>
                <TableCell className="font-medium">{area}</TableCell>
                <TableCell className="text-muted-foreground">{description}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <p className="mt-8 flex gap-3 rounded-xl border border-primary/40 bg-primary/5 p-5 text-sm text-muted-foreground">
        <Info className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden="true" />
        <span>
          <strong className="text-foreground">Key takeaway:</strong> Companies should set the course
          now — get processes, data and systems e-invoicing- and digital-reporting-ready for a
          compliant future in the EU single market.
        </span>
      </p>
    </section>
  );
}
